import React, { FC, Fragment, useContext, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AddEditDocumentBundleDialog from './add-edit-document-bundle-dialog/add-edit-document-bundle-dialog.component';
import { DocumentBundlesProvider, useDocumentBundles } from 'context/document-bundles-context';
import SkeletonTable, { SkeletonColumnConfig } from 'components/shared/skeleton/skeleton-table.component';
import { DocumentBundle } from 'models/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import PageTitle from 'components/shared/page-title/page-title.component';
import { bundleDocumentTypeTranslationKey } from './bundle-document-types';

const skeletonColumns: SkeletonColumnConfig[] = [
    { type: 'text', width: 150 },
    { type: 'text', width: 250 },
    { type: 'button', width: 80 },
];

const DocumentBundles: FC = () => {
    const { t: translate } = useTranslation(['lab-settings', 'orders', 'common']);
    const [bundleDialogOpen, setBundleDialogOpen] = useState(false);
    const [selectedBundle, setSelectedBundle] = useState<DocumentBundle>();
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const { documentBundles, documentBundlesLoading, deleteDocumentBundle } = useDocumentBundles();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const handleDialogClose = () => {
        setBundleDialogOpen(false);
    };

    const handleAddBundleClick = () => {
        setSelectedBundle(undefined);
        setBundleDialogOpen(true);
    };

    const handleEditBundleClick = (bundle: DocumentBundle) => {
        setSelectedBundle(bundle);
        setBundleDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!deleteConfirmId) return;
        const result = await deleteDocumentBundle({ documentBundleId: deleteConfirmId });
        setDeleteConfirmId(null);
        if (result) {
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:DOCUMENT_BUNDLE_DELETED_SUCCESSFULLY'),
            });
        }
    };

    const renderDocumentsSummary = (bundle: DocumentBundle): string =>
        bundle.documents
            ?.map((document) => {
                const label = translate(bundleDocumentTypeTranslationKey(document.documentType));
                return document.copies > 1 ? `${label} ×${document.copies}` : label;
            })
            .join(', ');

    return (
        <Fragment>
            <PageTitle headingStyle="h2" title={translate('lab-settings:DOCUMENT_BUNDLES')} />
            <Box mb={2}>{translate('lab-settings:DOCUMENT_BUNDLES_DESC')}</Box>
            <Box mb={1}>
                <Button startIcon={<AddIcon />} onClick={handleAddBundleClick}>
                    {translate('lab-settings:ADD_DOCUMENT_BUNDLE')}
                </Button>
            </Box>
            <Paper variant="outlined">
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{translate('lab-settings:DOCUMENT_BUNDLE_TITLE')}</TableCell>
                                <TableCell>{translate('lab-settings:DOCUMENTS')}</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {documentBundlesLoading && (
                                <SkeletonTable columns={skeletonColumns} rows={3} renderRowsOnly={true} />
                            )}
                            {documentBundles?.length > 0 &&
                                documentBundles.map((bundle) => (
                                    <TableRow
                                        key={bundle._id}
                                        hover
                                        onClick={() => handleEditBundleClick(bundle)}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>{bundle.title}</TableCell>
                                        <TableCell>{renderDocumentsSummary(bundle)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                disabled={documentBundlesLoading}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setDeleteConfirmId(bundle._id || '');
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            {!documentBundles?.length && !documentBundlesLoading && (
                                <TableRow>
                                    <TableCell colSpan={3} align="center">
                                        {translate('lab-settings:NO_DOCUMENT_BUNDLES_FOUND')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <AddEditDocumentBundleDialog
                documentBundle={selectedBundle}
                open={bundleDialogOpen}
                onDialogClose={handleDialogClose}
            />

            <ConfirmDialog
                open={!!deleteConfirmId}
                handleClose={() => setDeleteConfirmId(null)}
                handleConfirm={handleConfirmDelete}
                dialogTitle={translate('common:DELETE')}
                dialogContent={translate('lab-settings:CONFIRM_DELETE_DOCUMENT_BUNDLE')}
                confirmBtnLabel={translate('common:DELETE')}
            />
        </Fragment>
    );
};

const DocumentBundlesSettings: FC = () => (
    <DocumentBundlesProvider>
        <DocumentBundles />
    </DocumentBundlesProvider>
);

export default DocumentBundlesSettings;
